/**
 * ActionBannerHost — mounts once at the app root and renders the current
 * `actionBanner` from the notifications store. Fixed to the top of the viewport
 * below the nav so it's visible regardless of which page the user is on.
 *
 * Holds the banner while any notification modal is open so it doesn't compete
 * with modal content or expire unseen — auto-dismiss timer starts the moment
 * the last modal closes.
 */

import React from 'react';
import InlineBanner from '../InlineBanner';
import { useNotificationsStore } from '../../../store/notifications.store';

const ActionBannerHost: React.FC = () => {
  const banner = useNotificationsStore(s => s.actionBanner);
  const dismiss = useNotificationsStore(s => s.dismissActionBanner);

  // Gate the banner until no notification modal is open. Keeps the banner from
  // stealing focus over modals and prevents its auto-dismiss timer from running
  // while the user is still clicking through queued notifications.
  const anyModalOpen = useNotificationsStore(s =>
    s.showPendingLicensesModal ||
    s.showPendingRequestsModal ||
    s.showExpiredLicensesModal ||
    s.showPendingReviewsModal ||
    s.showReviewResultsModal ||
    s.showDeliveryNotificationsModal ||
    s.showDeliveryRejectionsModal ||
    s.showAccessRequestsModal ||
    s.showAccessRequestResultsModal ||
    s.showFtpAccessRequestsModal ||
    s.showFtpAccessResultsModal
  );

  if (!banner || anyModalOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1030,
        width: '90%',
        maxWidth: '560px',
        pointerEvents: 'auto',
      }}
    >
      <InlineBanner
        variant={banner.variant}
        title={banner.title}
        message={banner.message}
        items={banner.items}
        autoDismissMs={banner.autoDismissMs ?? 10000}
        onClose={dismiss}
        style={{ marginBottom: 0, boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}
      />
    </div>
  );
};

export default ActionBannerHost;
